/**
 * Custom Bottom-Controls-Bar (YouTube-style).
 *
 * Plan-Ref: /root/.claude/plans/check-deinen-plan-nochmal-snazzy-stream.md
 *
 * Replaces the native HTML5 `<video controls>` and the previous
 * `.player-overlay__gear` overlay. One bar — Play/Pause, Time,
 * Scrubber, Volume, Quality, Speed, Settings, Fullscreen — anchored
 * to the bottom of the stage so it stays visible inside
 * `stage.requestFullscreen()`.
 *
 * Only consumes ZapPlayer's PUBLIC API (setQuality, setSpeed,
 * togglePlayPause, toggleFullscreen, _toggleSettings, …) and the
 * native `<video>` element events. No knowledge of hls.js internals.
 *
 * Events wired:
 *   <video> play / pause / waiting / playing  → play/pause icon swap
 *   <video> timeupdate / durationchange       → scrubber + time labels
 *   <video> progress (buffered)               → scrubber buffered fill
 *   <video> volumechange                       → volume icon + slider
 *
 * Auto-hide: the bar fades out 2 s after the last `mousemove` over the
 * stage **iff** the video is actually playing. Pause / Settings-open /
 * hover-on-bar all keep it visible.
 */

const QUALITY_OPTIONS = [
  { value: 'original', label: 'Original (kein Transcode, LAN)' },
  { value: 'auto',     label: 'Auto (CPU-freundlich)'           },
  { value: '480',      label: '480p'                            },
  { value: '720',      label: '720p'                            },
  { value: '1080',     label: '1080p'                           },
  { value: 'max',      label: 'Source-Auflösung (transcoded)'   },
];
const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const FADE_DELAY_MS = 2000;

// Inline SVG paths — local to this module to keep cross-file deps minimal.
// Stroke is 2 px to match icons.js conventions.
const ICONS = {
  play:           '<path d="M7 4v16l13 -8z"/>',
  pause:          '<path d="M7 5v14"/><path d="M17 5v14"/>',
  volume:         '<path d="M15 8a5 5 0 0 1 0 8"/><path d="M17.7 5a9 9 0 0 1 0 14"/><path d="M6 15h-2a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2l3.5-4.5a.8.8 0 0 1 1.5.5v14a.8.8 0 0 1-1.5.5z"/>',
  volumeMute:     '<path d="M6 15h-2a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2l3.5-4.5a.8.8 0 0 1 1.5.5v14a.8.8 0 0 1-1.5.5z"/><path d="M16 10l4 4m0-4l-4 4"/>',
  fullscreen:     '<path d="M4 8v-3a1 1 0 0 1 1-1h3"/><path d="M4 16v3a1 1 0 0 0 1 1h3"/><path d="M16 4h3a1 1 0 0 1 1 1v3"/><path d="M16 20h3a1 1 0 0 0 1-1v-3"/>',
  fullscreenExit: '<path d="M5 9h3a1 1 0 0 0 1-1v-3"/><path d="M5 15h3a1 1 0 0 1 1 1v3"/><path d="M19 9h-3a1 1 0 0 1-1-1v-3"/><path d="M19 15h-3a1 1 0 0 0-1 1v3"/>',
  settings:       '<path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37c1 .608 2.296.07 2.572-1.065z"/><circle cx="12" cy="12" r="3"/>',
  cast:           '<path d="M3 19a8 8 0 0 1 8 8"/><path d="M3 14a13 13 0 0 1 13 13"/><path d="M3 9a18 18 0 0 1 18 18"/><path d="M3 5h18a2 2 0 0 1 2 2v8"/><path d="M3 17v.01"/>',
  pip:            '<rect x="3" y="5" width="18" height="14" rx="2"/><rect x="11" y="11" width="8" height="6" rx="1" fill="currentColor"/>',
  stats:          '<path d="M4 19h16"/><rect x="6" y="13" width="3" height="6"/><rect x="11" y="9" width="3" height="10"/><rect x="16" y="5" width="3" height="14"/>',
  // Audio-track + Subtitle-track icons (added 2026-05-04 with multi-track UI):
  // audio = stylised speech/sound waves; subs = ⓒⓒ-style closed-caption box.
  audio:          '<path d="M3 9v6h4l5 4V5L7 9H3z"/><path d="M16 8a5 5 0 0 1 0 8"/>',
  subs:           '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M7 14h3"/><path d="M14 14h3"/><path d="M7 11h2"/><path d="M13 11h4"/>',
};

function svg(iconName) {
  const path = ICONS[iconName] || '';
  return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
}

function fmtHms(seconds) {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad2 = (n) => (n < 10 ? '0' + n : '' + n);
  return h > 0 ? `${h}:${pad2(m)}:${pad2(ss)}` : `${m}:${pad2(ss)}`;
}

function qualityLabel(value) {
  switch (value) {
    case 'original': return 'Original';
    case 'auto':     return 'Auto';
    case 'max':      return 'Source';
    case '480':      return '480p';
    case '720':      return '720p';
    case '1080':     return '1080p';
    default:         return String(value || 'Auto');
  }
}

/**
 * @param {Object} player   ZapPlayer instance — uses .video, .stage, setQuality,
 *                          setSpeed, togglePlayPause, toggleFullscreen, _toggleSettings,
 *                          playerSettings.
 * @param {HTMLElement} hostEl  The `<div data-slot="controls">` from createModalShell.
 * @returns {{el:HTMLElement, dispose:()=>void, updateLive:(info:Object)=>void,
 *            setQualityLabel:(s:string)=>void, setSpeedLabel:(s:string)=>void}}
 */
export function createControls(player, hostEl, _opts = {}) {
  const video = player.video;
  const stage = player.stage;

  const initialQuality = player.playerSettings?.quality || 'auto';
  const initialSpeed   = player.playerSettings?.speed   || 1;

  hostEl.innerHTML = `
    <div class="po-controls__scrubber-row">
      <div class="po-controls__scrubber" role="slider" tabindex="0"
           aria-label="Position im Video">
        <div class="po-controls__scrubber__buffered"></div>
        <div class="po-controls__scrubber__progress"></div>
        <div class="po-controls__scrubber__thumb"></div>
        <div class="po-controls__scrubber__thumbpreview" hidden></div>
        <div class="po-controls__scrubber__tooltip" hidden>0:00</div>
      </div>
    </div>
    <div class="po-controls__row">
      <button class="po-controls__btn po-controls__btn--play" type="button"
              aria-label="Wiedergabe starten/pausieren" title="Play/Pause (Space)">
        ${svg('play')}
      </button>
      <span class="po-controls__time" data-slot="time">0:00 / 0:00</span>
      <div class="po-controls__volume">
        <button class="po-controls__btn po-controls__btn--volume" type="button"
                aria-label="Stumm an/aus" title="Stumm (M)">
          ${svg('volume')}
        </button>
        <input type="range" class="po-controls__volume__slider"
               min="0" max="1" step="0.01" value="${video.muted ? 0 : video.volume}"
               aria-label="Lautstärke">
      </div>
      <span class="po-controls__spacer"></span>
      <div class="po-controls__audio">
        <button class="po-controls__audio__btn" type="button"
                aria-haspopup="true" aria-expanded="false" title="Tonspur wählen">
          ${svg('audio')}
          <span data-slot="audio-label">Tonspur</span>
          <span class="po-controls__caret">▾</span>
        </button>
        <div class="po-controls__menu po-controls__menu--audio" hidden role="menu">
          <div class="po-controls__menu__title">Tonspur</div>
          <div class="po-controls__menu__item is-active" aria-disabled="true" role="menuitemradio" aria-checked="true">Lädt…</div>
        </div>
      </div>
      <div class="po-controls__subs" hidden>
        <button class="po-controls__subs__btn" type="button"
                aria-haspopup="true" aria-expanded="false" title="Untertitel wählen">
          ${svg('subs')}
          <span data-slot="subs-label">Aus</span>
          <span class="po-controls__caret">▾</span>
        </button>
        <div class="po-controls__menu po-controls__menu--subs" hidden role="menu">
          <div class="po-controls__menu__title">Untertitel</div>
        </div>
      </div>
      <div class="po-controls__quality">
        <button class="po-controls__quality__btn" type="button"
                aria-haspopup="true" aria-expanded="false"
                title="Auflösung wählen — hover für Live-Format">
          <span data-slot="quality-label">${qualityLabel(initialQuality)}</span>
          <span class="po-controls__caret">▾</span>
        </button>
        <div class="po-controls__menu po-controls__menu--quality" hidden role="menu">
          <div class="po-controls__menu__title">Auflösung</div>
          ${QUALITY_OPTIONS.map(o => `
            <button type="button" data-quality="${o.value}"
              class="po-controls__menu__item${o.value === initialQuality ? ' is-active' : ''}"
              role="menuitemradio" aria-checked="${o.value === initialQuality}">
              ${o.label}
            </button>`).join('')}
        </div>
        <div class="po-controls__live-tooltip" hidden>
          <div data-slot="live-line">Spielt: —</div>
          <div class="src-line" data-slot="src-line">Quelle: —</div>
        </div>
      </div>
      <div class="po-controls__speed">
        <button class="po-controls__speed__btn" type="button"
                aria-haspopup="true" aria-expanded="false" title="Wiedergabe-Geschwindigkeit">
          <span data-slot="speed-label">${initialSpeed}×</span>
          <span class="po-controls__caret">▾</span>
        </button>
        <div class="po-controls__menu po-controls__menu--speed" hidden role="menu">
          <div class="po-controls__menu__title">Geschwindigkeit</div>
          ${SPEED_OPTIONS.map(s => `
            <button type="button" data-speed="${s}"
              class="po-controls__menu__item${s === initialSpeed ? ' is-active' : ''}"
              role="menuitemradio" aria-checked="${s === initialSpeed}">${s}×</button>`).join('')}
        </div>
      </div>
      <button class="po-controls__btn po-controls__btn--cast is-unavailable" type="button"
              data-cast-launcher
              aria-label="Auf Chromecast / AirPlay übertragen" title="Cast / Chromecast">
        ${svg('cast')}
      </button>
      <button class="po-controls__btn po-controls__btn--pip" type="button"
              aria-label="Picture-in-Picture" title="Picture-in-Picture (P)">
        ${svg('pip')}
      </button>
      <button class="po-controls__btn po-controls__btn--stats" type="button"
              aria-label="Statistiken" title="Statistiken (S)">
        ${svg('stats')}
      </button>
      <button class="po-controls__btn po-controls__btn--settings" type="button"
              aria-label="Einstellungen" title="Einstellungen">
        ${svg('settings')}
      </button>
      <button class="po-controls__btn po-controls__btn--fullscreen" type="button"
              aria-label="Vollbild" title="Vollbild (F)">
        ${svg('fullscreen')}
      </button>
    </div>
  `;

  const $ = (sel) => hostEl.querySelector(sel);
  const $$ = (sel) => hostEl.querySelectorAll(sel);

  const playBtn      = $('.po-controls__btn--play');
  const playIconSlot = playBtn;
  const timeLabel    = $('[data-slot="time"]');
  const scrubber     = $('.po-controls__scrubber');
  const buffered     = $('.po-controls__scrubber__buffered');
  const progress     = $('.po-controls__scrubber__progress');
  const thumb        = $('.po-controls__scrubber__thumb');
  const thumbPreview = $('.po-controls__scrubber__thumbpreview');
  const tooltip      = $('.po-controls__scrubber__tooltip');
  const volBtn       = $('.po-controls__btn--volume');
  const volSlider    = $('.po-controls__volume__slider');
  const qualityBtn   = $('.po-controls__quality__btn');
  const qualityLbl   = $('[data-slot="quality-label"]');
  const qualityMenu  = $('.po-controls__menu--quality');
  const speedBtn     = $('.po-controls__speed__btn');
  const speedLbl     = $('[data-slot="speed-label"]');
  const speedMenu    = $('.po-controls__menu--speed');
  const audioBox     = $('.po-controls__audio');
  const audioBtn     = $('.po-controls__audio__btn');
  const audioLbl     = $('[data-slot="audio-label"]');
  const audioMenu    = $('.po-controls__menu--audio');
  const subsBox      = $('.po-controls__subs');
  const subsBtn      = $('.po-controls__subs__btn');
  const subsLbl      = $('[data-slot="subs-label"]');
  const subsMenu     = $('.po-controls__menu--subs');
  const _castBtn     = $('.po-controls__btn--cast');
  const pipBtn       = $('.po-controls__btn--pip');
  const statsBtn     = $('.po-controls__btn--stats');
  const settingsBtn  = $('.po-controls__btn--settings');
  const fullscreenBtn= $('.po-controls__btn--fullscreen');
  const liveTooltip  = $('.po-controls__live-tooltip');
  const liveLine     = $('[data-slot="live-line"]');
  const srcLine      = $('[data-slot="src-line"]');

  // ── Auto-hide ──────────────────────────────────────────────────────────────
  let fadeTimer = null;
  function showBar() {
    hostEl.classList.remove('is-hidden');
    if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
    if (!video.paused && !anyMenuOpen()) {
      fadeTimer = setTimeout(() => hostEl.classList.add('is-hidden'), FADE_DELAY_MS);
    }
  }
  function anyMenuOpen() {
    return !qualityMenu.hidden || !speedMenu.hidden
        || !audioMenu.hidden   || !subsMenu.hidden;
  }
  stage.addEventListener('mousemove',  showBar);
  stage.addEventListener('mouseleave', showBar);   // also pings to (re-)start the fade timer if appropriate
  hostEl.addEventListener('mouseenter', () => {
    if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
    hostEl.classList.remove('is-hidden');
  });
  hostEl.addEventListener('mouseleave', showBar);

  // ── Play / Pause ──────────────────────────────────────────────────────────
  function setPlayIcon(playing) {
    playIconSlot.innerHTML = svg(playing ? 'pause' : 'play');
  }
  setPlayIcon(!video.paused);
  playBtn.addEventListener('click', () => {
    try { player.togglePlayPause?.(); } catch {}
  });
  video.addEventListener('play',     () => { setPlayIcon(true);  showBar(); });
  video.addEventListener('pause',    () => { setPlayIcon(false); showBar(); });
  video.addEventListener('playing',  () => { setPlayIcon(true);  showBar(); });

  // ── Time + Scrubber ───────────────────────────────────────────────────────
  // 2026-05-08: scrubber refactor — pointer-events + drag-only-preview.
  //   - drag: thumb + tooltip folgen cursor, NIEMALS seek während drag
  //   - release: einmaliger seek auf drop-position, spinner bis 'seeked'
  //   - click = degenerate drag (down→up am gleichen X)
  //   - setPointerCapture: drag bleibt aktiv auch wenn cursor scrubber
  //     verlässt (sonst stuck-drag bei out-of-bounds)
  //   - rAF-coalesce: high-end mice fire 1000 Hz, präsentation 60 Hz
  //   - touch-action: none in CSS (mobile)
  //
  // Pre-fix: jeder mousemove rief player.seekTo() → echter engine-seek
  // pro pixel mausbewegung → decoder-thrashing + WebCodecs#867 keyframe-
  // required-after-flush errors. Plus updateTime's thumb-snap-back
  // während drag (engine.currentTimeSec changes mid-seek).
  let scrubbing    = false;
  let awaitingSeek = false;
  let pendingSec   = 0;
  let rafQueued    = false;
  let lastPointerEvent = null;

  function updateTime() {
    const cur = video.currentTime || 0;
    const dur = (isFinite(video.duration) && video.duration > 0) ? video.duration : 0;
    if (dur > 0) {
      timeLabel.textContent = `${fmtHms(cur)} / ${fmtHms(dur)}`;
      timeLabel.classList.remove('is-loading');
    } else {
      // Encoder-warmup: duration not yet known. Pulse-animate `—:—`
      // so the user sees there's progress (otherwise the player feels
      // frozen during the 5–15 s HEVC HDR transcode warmup).
      timeLabel.textContent = `${fmtHms(cur)} / —:—`;
      timeLabel.classList.add('is-loading');
    }
    // Skip thumb update while user is actively dragging OR seek is
    // pending — der user-drag oder die pinned drop-position würde
    // sonst durch engine.currentTimeSec überschrieben (snap-back-bug,
    // siehe video.js PR #6372 für gleiches problem dort).
    if (scrubbing || awaitingSeek) return;
    if (dur > 0) {
      const pct = Math.max(0, Math.min(100, (cur / dur) * 100));
      progress.style.width = pct + '%';
      thumb.style.left = pct + '%';
    } else {
      progress.style.width = '0%';
      thumb.style.left = '0%';
    }
  }
  function updateBuffered() {
    const dur = (isFinite(video.duration) && video.duration > 0) ? video.duration : 0;
    if (dur <= 0) { buffered.style.width = '0%'; return; }
    const ranges = video.buffered;
    if (!ranges || ranges.length === 0) { buffered.style.width = '0%'; return; }
    // Take the buffered range that contains currentTime, or the first one.
    const cur = video.currentTime || 0;
    let end = 0;
    for (let i = 0; i < ranges.length; i++) {
      if (ranges.start(i) <= cur && ranges.end(i) >= cur) { end = ranges.end(i); break; }
      if (i === 0) end = ranges.end(0);
    }
    buffered.style.width = Math.min(100, (end / dur) * 100) + '%';
  }
  video.addEventListener('timeupdate',     updateTime);
  video.addEventListener('durationchange', updateTime);
  video.addEventListener('loadedmetadata', () => { updateTime(); updateBuffered(); });
  video.addEventListener('progress',       updateBuffered);
  // 2026-05-08: zapd-engine rendert in canvas+webgpu — das <video>-element
  // bekommt nie media-source → keiner der obigen events feuert. Trotzdem
  // muss der user SOFORT bei modal-open feedback sehen statt statisches
  // "0:00 / 0:00". Initial-paint mit pulse-class.
  updateTime();
  // After seek completes, allow updateTime to resume thumb-positioning.
  // 'seeked' wird vom zapd-engine-bridge dispatched bei seekComplete.
  video.addEventListener('seeked', () => { awaitingSeek = false; });

  function pctFromEvent(evt) {
    const rect = scrubber.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }
  function applyPreview() {
    if (!lastPointerEvent) return;
    const pct = pctFromEvent(lastPointerEvent);
    const dur = (isFinite(video.duration) && video.duration > 0) ? video.duration : 0;
    pendingSec = pct * dur;
    const pctStr = (pct * 100) + '%';
    progress.style.width = pctStr;
    thumb.style.left     = pctStr;
    if (tooltip) {
      tooltip.textContent = fmtHms(pendingSec);
      tooltip.style.left  = pctStr;
      tooltip.hidden      = false;
    }
  }
  function commitSeek() {
    if (!scrubbing) return;
    scrubbing    = false;
    awaitingSeek = true;
    if (tooltip) tooltip.hidden = true;
    // Drag may end outside the scrubber (pointer capture) → no pointerleave.
    if (!hoverActive) hideThumbPreview();
    const dur = (isFinite(video.duration) && video.duration > 0) ? video.duration : 0;
    if (dur > 0) {
      // Spinner-overlay wird von player.seekTo() automatisch eingeblendet
      // und nach engine.firstFrame (= waiting→playing transition) wieder
      // ausgeblendet via _hideSeekingOverlayWhenPlaying().
      try { player.seekTo(pendingSec); } catch {}
    } else {
      awaitingSeek = false;
    }
  }
  scrubber.addEventListener('pointerdown', (e) => {
    if (e.button !== undefined && e.button !== 0) return;  // primary only
    // 2026-05-08: gate on player._seekReady. While the keyframe-cache
    // is still being scanned (cold-cache-miss), seek isn't possible —
    // encoder doesn't know IDR-positions. Scrubber stays read-only
    // until ready. zap-player._applySeekReadyUI toggles `.is-indexing`
    // class on the scrubber for visual feedback.
    if (player?._seekReady === false) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    try { scrubber.setPointerCapture(e.pointerId); } catch { /* noop */ }
    scrubbing = true;
    lastPointerEvent = e;
    applyPreview();
  });
  scrubber.addEventListener('pointermove', (e) => {
    if (!scrubbing) return;
    lastPointerEvent = e;
    if (rafQueued) return;
    rafQueued = true;
    requestAnimationFrame(() => { rafQueued = false; applyPreview(); });
  });
  scrubber.addEventListener('pointerup', (e) => {
    if (!scrubbing) return;
    try { scrubber.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    commitSeek();
  });
  scrubber.addEventListener('pointercancel', () => {
    // Aborted drag (page-hide, system-interrupt, browser cancel).
    // Abandon without seek; let updateTime restore thumb to engine pos.
    scrubbing    = false;
    awaitingSeek = false;
    if (tooltip) tooltip.hidden = true;
    hideThumbPreview();
    updateTime();
  });

  // ── Scrub thumbnails (B6 storyboard preview) ─────────────────────────────
  // Server contract (Plan B6): <sessionBase>/thumbs/index.json describes
  // 10x10 JPEG sheets; thumb i covers [i*interval, (i+1)*interval), lives on
  // sheet floor(i/(cols*rows)) at col=i%cols, row=floor((i%(cols*rows))/cols).
  // Everything here is best-effort: any failure keeps the box hidden and the
  // scrubber behaves exactly like today (time-tooltip only). index.json is
  // fetched lazily on the FIRST hover, never at mount; while it 404s (server
  // still generating) or reports complete:false, re-polls are hover-driven
  // and throttled to one attempt per 5 s — no background timer, and a hidden
  // tab gets no pointer events (core/visibility.js philosophy holds for free).
  const THUMBS_POLL_MS = 5000;

  let thumbsLoc        = null;   // { srcUrl, base, query } pinned after a 200 index fetch
  let thumbsMeta       = null;   // validated index.json payload
  let thumbsOff        = false;  // hard-off: malformed index / unusable schema
  let thumbsFetching   = false;
  let thumbsNextPollAt = 0;      // Date.now() gate for the next index attempt
  let thumbsAbort      = null;   // AbortController of the in-flight index fetch
  let sheetImg         = null;   // Image() preloading the sheet under the cursor
  let sheetImgUrl      = null;
  const sheetsReady    = new Set(); // sheet URLs that finished loading (anti-flicker gate)
  let shownSheetUrl    = null;   // sheet currently painted as background
  let hoverActive      = false;
  let lastHoverEvent   = null;
  let thumbRafQueued   = false;

  function thumbsSessionSrc() {
    // The session/manifest URL the engine plays from. ZapPlayer keeps the
    // /play/<sid>/<gen>/master.m3u8 from POST /play/start on _hlsSession;
    // the MainBridge instance (window.__zapEngine) carries the same URL in
    // its ctor options. Either yields <sessionBase> = the part before /<gen>/.
    return player?._hlsSession?.playlistUrl
        || window.__zapEngine?.opts?.manifestUrl
        || '';
  }

  function tokenQueryFromSearch(search) {
    try {
      const p = new URLSearchParams(search || '');
      const e = p.get('e'), st = p.get('st');
      if (e && st) return '?e=' + encodeURIComponent(e) + '&st=' + encodeURIComponent(st);
    } catch { /* noop */ }
    return '';
  }

  // The master.m3u8 URL itself carries no ?e=&st= — the server signs the
  // segment token only into the VARIANT playlists' URIs (EXT-X-MAP + segment
  // lines). Manifests are token-exempt (cookie/ownership-gated), so scrape:
  // master → first referenced variant playlist → first tokenised URI.
  async function scrapeTokenQuery(manifestUrl, signal) {
    const fetchText = async (url) => {
      const r = await fetch(url, { credentials: 'same-origin', signal });
      if (!r.ok) throw new Error('manifest ' + r.status);
      return await r.text();
    };
    const tokenIn = (text) => {
      const m = /[?&]e=(\d+)&st=([A-Za-z0-9_-]+)/.exec(text);
      return m ? '?e=' + m[1] + '&st=' + m[2] : '';
    };
    try {
      const master = await fetchText(manifestUrl.toString());
      const direct = tokenIn(master);
      if (direct) return direct;
      const refs = [];
      const uriRe = /URI="([^"]*\.m3u8[^"]*)"/g;
      for (let m; (m = uriRe.exec(master)); ) refs.push(m[1]);
      for (const line of master.split('\n')) {
        const t = line.trim();
        if (t && !t.startsWith('#') && t.includes('.m3u8')) refs.push(t);
      }
      if (refs.length === 0) return '';
      const variant = await fetchText(new URL(refs[0], manifestUrl).toString());
      return tokenIn(variant);
    } catch {
      return '';
    }
  }

  async function resolveThumbsLoc(signal) {
    const src = thumbsSessionSrc();
    if (!src) return null;                       // no session (yet) → retry on later hover
    if (thumbsLoc && thumbsLoc.srcUrl === src) return thumbsLoc;
    let abs;
    try { abs = new URL(src, location.href); } catch { return null; }
    // sessionBase = manifest URL minus the /<gen>/<file> tail.
    const m = /^(.+)\/\d+\/[^/]+$/.exec(abs.pathname);
    if (!m) return null;
    let query = tokenQueryFromSearch(abs.search);
    if (!query) query = await scrapeTokenQuery(abs, signal);
    // Not cached here: pinned only once an index fetch succeeds with it, so
    // a failed token-scrape (e.g. during warmup) is retried next poll.
    return { srcUrl: src, base: abs.origin + m[1] + '/thumbs/', query };
  }

  function validThumbsIndex(j) {
    return !!j && j.version === 1
        && Number(j.interval_sec) > 0
        && Number(j.thumb_w) > 0 && Number(j.thumb_h) > 0
        && Number(j.cols) > 0 && Number(j.rows) > 0
        && Array.isArray(j.sheets);
  }

  function maybeFetchThumbsIndex() {
    if (thumbsOff || thumbsFetching) return;
    if (thumbsMeta && thumbsMeta.complete
        && thumbsLoc && thumbsLoc.srcUrl === thumbsSessionSrc()) return;
    const now = Date.now();
    if (now < thumbsNextPollAt) return;
    if (document.hidden) return;
    thumbsNextPollAt = now + THUMBS_POLL_MS;
    thumbsFetching = true;
    thumbsAbort = new AbortController();
    const signal = thumbsAbort.signal;
    (async () => {
      const loc = await resolveThumbsLoc(signal);
      if (!loc) return;
      const r = await fetch(loc.base + 'index.json' + loc.query,
                            { credentials: 'same-origin', signal });
      if (!r.ok) return;                         // 404/403 while generating → next poll
      const j = await r.json();
      if (!validThumbsIndex(j)) { thumbsOff = true; return; }
      thumbsMeta = j;
      thumbsLoc  = loc;                          // pin the location that worked
    })().catch(() => { /* network error / abort → feature stays dormant */ })
       .finally(() => { thumbsFetching = false; thumbsAbort = null; });
  }

  function hideThumbPreview() {
    thumbPreview.hidden = true;
  }

  function showThumbAt(pct) {
    if (thumbsOff || !thumbsMeta || !thumbsLoc) { hideThumbPreview(); return; }
    const meta = thumbsMeta;
    const dur = (isFinite(video.duration) && video.duration > 0) ? video.duration : 0;
    if (dur <= 0) { hideThumbPreview(); return; }
    const perSheet = meta.cols * meta.rows;
    const count = Number(meta.count) || 0;       // absent/garbage → 0 → hidden
    let i = Math.floor((pct * dur) / meta.interval_sec);
    if (i < 0) i = 0;
    if (meta.complete && count > 0 && i >= count) i = count - 1;
    const sheetIdx = Math.floor(i / perSheet);
    if (i >= count || sheetIdx >= meta.sheets.length) {
      // Thumb/sheet not generated yet (index still growing) → tooltip only.
      hideThumbPreview();
      return;
    }
    const url  = thumbsLoc.base + meta.sheets[sheetIdx] + thumbsLoc.query;
    const cell = i % perSheet;
    const col  = cell % meta.cols;
    const row  = Math.floor(cell / meta.cols);

    // Horizontal clamp so the box never sticks out of the scrubber width.
    const rect = scrubber.getBoundingClientRect();
    const half = meta.thumb_w / 2;
    const x = Math.max(half, Math.min(Math.max(half, rect.width - half), pct * rect.width));
    thumbPreview.style.width  = meta.thumb_w + 'px';
    thumbPreview.style.height = meta.thumb_h + 'px';
    thumbPreview.style.left   = x + 'px';

    if (sheetsReady.has(url)) {
      thumbPreview.style.backgroundImage    = `url("${url}")`;
      thumbPreview.style.backgroundSize     = `${meta.cols * meta.thumb_w}px ${meta.rows * meta.thumb_h}px`;
      thumbPreview.style.backgroundPosition = `${-col * meta.thumb_w}px ${-row * meta.thumb_h}px`;
      shownSheetUrl = url;
      thumbPreview.hidden = false;
    } else {
      preloadSheet(url);
      // Anti-flicker: keep the previously painted sheet (at its last
      // position) until the new one has loaded; a briefly stale image
      // during a sheet-crossing drag is acceptable (plan B6). If nothing
      // was painted yet, stay hidden.
      if (shownSheetUrl) thumbPreview.hidden = false;
      else hideThumbPreview();
    }
  }

  function preloadSheet(url) {
    if (sheetsReady.has(url) || sheetImgUrl === url) return;
    if (sheetImg) { sheetImg.onload = null; sheetImg.onerror = null; }
    const img = new Image();
    sheetImg = img;
    sheetImgUrl = url;
    img.onload = () => {
      sheetsReady.add(url);
      if (sheetImg === img) { sheetImg = null; sheetImgUrl = null; }
      refreshThumbFromPointer();
    };
    img.onerror = () => {
      if (sheetImg === img) { sheetImg = null; sheetImgUrl = null; }
    };
    img.src = url;
  }

  function refreshThumbFromPointer() {
    const evt = scrubbing ? lastPointerEvent : (hoverActive ? lastHoverEvent : null);
    if (!evt) return;
    showThumbAt(pctFromEvent(evt));
  }

  function onScrubberHover(e) {
    hoverActive = true;
    lastHoverEvent = e;
    // A session that was replaced mid-play (refreshSession → new sid) makes
    // the cached location stale; drop state so the next poll re-derives it.
    const src = thumbsSessionSrc();
    if (thumbsLoc && src && thumbsLoc.srcUrl !== src) {
      thumbsLoc = null; thumbsMeta = null; thumbsOff = false;
      shownSheetUrl = null; thumbsNextPollAt = 0;
      hideThumbPreview();
    }
    maybeFetchThumbsIndex();
    if (thumbRafQueued) return;                  // rAF-coalesce (1000 Hz mice)
    thumbRafQueued = true;
    requestAnimationFrame(() => { thumbRafQueued = false; refreshThumbFromPointer(); });
  }
  scrubber.addEventListener('pointerenter', onScrubberHover);
  scrubber.addEventListener('pointermove',  onScrubberHover);
  scrubber.addEventListener('pointerleave', () => {
    hoverActive = false;
    lastHoverEvent = null;
    if (!scrubbing) hideThumbPreview();
  });

  // ── Volume ────────────────────────────────────────────────────────────────
  function syncVolumeUi() {
    const muted = video.muted || video.volume === 0;
    volBtn.innerHTML = svg(muted ? 'volumeMute' : 'volume');
    volSlider.value = muted ? 0 : video.volume;
  }
  syncVolumeUi();
  volBtn.addEventListener('click', () => {
    video.muted = !video.muted;
    if (!video.muted && video.volume === 0) video.volume = 0.5;
  });
  volSlider.addEventListener('input', () => {
    const v = parseFloat(volSlider.value);
    video.volume = v;
    video.muted  = (v === 0);
  });
  video.addEventListener('volumechange', syncVolumeUi);

  // ── Picture-in-Picture ────────────────────────────────────────────────────
  // Hide the button entirely on browsers that don't support PiP (iOS Safari
  // implements a different API; the helper in zap-player.js handles both).
  if (!document.pictureInPictureEnabled) {
    pipBtn.hidden = true;
  } else {
    pipBtn.addEventListener('click', () => {
      try { player.togglePip?.(); } catch {}
    });
    video.addEventListener('enterpictureinpicture', () => pipBtn.classList.add('is-on'));
    video.addEventListener('leavepictureinpicture', () => pipBtn.classList.remove('is-on'));
  }

  // ── Stats overlay ─────────────────────────────────────────────────────────
  statsBtn.addEventListener('click', () => {
    try { player.toggleStats?.(); } catch {}
    statsBtn.classList.toggle('is-on', !!player.isStatsVisible?.());
  });

  // Cast button (data-cast-launcher) is wired from zap-player.js via
  // attachCastButtons(); that helper toggles `hidden` based on the CAF
  // state listener (CAST_AVAILABLE / CONNECTED / NOT_CONNECTED) and adds
  // .is-on while connected. No click handler here.

  // ── Quality menu ──────────────────────────────────────────────────────────
  function closeAllMenus() {
    qualityMenu.hidden = true; speedMenu.hidden = true;
    audioMenu.hidden = true;   subsMenu.hidden = true;
    updateAriaExpanded();
  }
  function updateAriaExpanded() {
    qualityBtn.setAttribute('aria-expanded', String(!qualityMenu.hidden));
    speedBtn.setAttribute('aria-expanded',   String(!speedMenu.hidden));
    audioBtn.setAttribute('aria-expanded',   String(!audioMenu.hidden));
    subsBtn.setAttribute('aria-expanded',    String(!subsMenu.hidden));
  }
  qualityBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const next = qualityMenu.hidden;
    closeAllMenus();
    qualityMenu.hidden = !next;
    updateAriaExpanded();
  });
  qualityMenu.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quality]');
    if (!btn) return;
    const value = btn.dataset.quality;
    qualityMenu.hidden = true; updateAriaExpanded();
    qualityLbl.textContent = qualityLabel(value);
    $$('[data-quality]').forEach((b) => {
      const active = b.dataset.quality === value;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', String(active));
    });
    try { player.setQuality?.(value); } catch {}
  });

  // ── Speed menu ────────────────────────────────────────────────────────────
  speedBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const next = speedMenu.hidden;
    closeAllMenus();
    speedMenu.hidden = !next;
    updateAriaExpanded();
  });
  speedMenu.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-speed]');
    if (!btn) return;
    const value = parseFloat(btn.dataset.speed);
    speedMenu.hidden = true; updateAriaExpanded();
    speedLbl.textContent = `${value}×`;
    $$('[data-speed]').forEach((b) => {
      const active = parseFloat(b.dataset.speed) === value;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', String(active));
    });
    try { player.setSpeed?.(value); } catch {}
  });

  // ── Audio-track menu ──────────────────────────────────────────────────────
  // Items are injected by setStreams() once /api/probe resolves; until then
  // the whole .po-controls__audio container stays `hidden`.
  audioBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const next = audioMenu.hidden;
    closeAllMenus();
    audioMenu.hidden = !next;
    updateAriaExpanded();
  });
  audioMenu.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-audio-track]');
    if (!btn || btn.disabled) return;
    const idx = parseInt(btn.dataset.audioTrack, 10);
    if (!Number.isFinite(idx)) return;
    audioMenu.hidden = true; updateAriaExpanded();
    audioLbl.textContent = btn.dataset.label || `Spur ${idx + 1}`;
    audioMenu.querySelectorAll('[data-audio-track]').forEach((b) => {
      const active = parseInt(b.dataset.audioTrack, 10) === idx;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', String(active));
    });
    try { player.setAudioTrack?.(idx); } catch {}
  });

  // ── Subtitle-track menu ───────────────────────────────────────────────────
  subsBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const next = subsMenu.hidden;
    closeAllMenus();
    subsMenu.hidden = !next;
    updateAriaExpanded();
  });
  subsMenu.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-subs-track]');
    if (!btn || btn.disabled) return;
    const idx = parseInt(btn.dataset.subsTrack, 10);
    if (!Number.isFinite(idx)) return;
    subsMenu.hidden = true; updateAriaExpanded();
    subsLbl.textContent = btn.dataset.label || (idx < 0 ? 'Aus' : `#${idx}`);
    subsBtn.classList.toggle('is-on', idx >= 0);
    subsMenu.querySelectorAll('[data-subs-track]').forEach((b) => {
      const active = parseInt(b.dataset.subsTrack, 10) === idx;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', String(active));
    });
    try { player.setSubtitleTrack?.(idx); } catch {}
  });

  // Click outside any open menu → close it.
  function onDocClick(e) {
    if (!hostEl.contains(e.target)) closeAllMenus();
    else if (!e.target.closest('.po-controls__quality') &&
             !e.target.closest('.po-controls__speed')   &&
             !e.target.closest('.po-controls__audio')   &&
             !e.target.closest('.po-controls__subs')) closeAllMenus();
  }
  document.addEventListener('click', onDocClick, true);

  // ── Settings + Fullscreen ─────────────────────────────────────────────────
  settingsBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    closeAllMenus();
    try { player._toggleSettings?.(); } catch {}
  });
  function syncFullscreenIcon() {
    const fs = !!document.fullscreenElement;
    fullscreenBtn.innerHTML = svg(fs ? 'fullscreenExit' : 'fullscreen');
    fullscreenBtn.setAttribute('aria-label', fs ? 'Vollbild beenden' : 'Vollbild');
  }
  syncFullscreenIcon();
  fullscreenBtn.addEventListener('click', () => {
    try { player.toggleFullscreen?.(); } catch {}
  });
  document.addEventListener('fullscreenchange', syncFullscreenIcon);

  // ── Live-info tooltip on the Quality button (User-Wahl: Tooltip) ──────────
  // Mouse-driven. Touch users won't see it; that's fine — the same data is
  // also available from the Settings panel via "Statistiken".
  const qualityRow = hostEl.querySelector('.po-controls__quality');
  qualityRow.addEventListener('mouseenter', () => { liveTooltip.hidden = false; });
  qualityRow.addEventListener('mouseleave', () => { liveTooltip.hidden = true;  });

  function updateLive(info) {
    info = info || {};
    const lineParts = [];
    if (info.codec) lineParts.push(String(info.codec));
    if (info.width && info.height) lineParts.push(`${info.width}×${info.height}`);
    if (info.bitrate) {
      const mb = (Number(info.bitrate) / 1_000_000).toFixed(1);
      lineParts.push(`${mb} Mb/s`);
    }
    if (info.audioCodec) lineParts.push(String(info.audioCodec));
    liveLine.textContent = 'Spielt: ' + (lineParts.length ? lineParts.join(' · ') : '—');

    const srcParts = [];
    if (info.srcCodec) srcParts.push(String(info.srcCodec));
    if (info.srcWidth && info.srcHeight) srcParts.push(`${info.srcWidth}×${info.srcHeight}`);
    if (info.srcAudio) srcParts.push(String(info.srcAudio));
    srcLine.textContent = 'Quelle: ' + (srcParts.length ? srcParts.join(' · ') : '—');
  }

  // ── Audio + Subtitle stream-list population (called by ZapPlayer once
  // /api/probe resolves). Hides the whole container when there's nothing
  // useful to pick: single audio stream → no audio button; zero subs → no
  // subs button. Rendering both `<select>` markup and the menu items via
  // the same data so the labels stay in sync with what the user sees in
  // the dropdown.
  function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, c =>
      ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  // 2026-05-08: BluRay/UHD audio-tracks come from libavformat without
  // language tags (libbluray doesn't propagate MPLS/CLPI lang to libav
  // metadata). Show "Spur N" with ordinal + rich codec/profile/channel
  // info instead of "UND" placeholder.
  function languageLabel(s, fallbackOrdinal) {
    if (s.language && s.language.trim() && s.language.toLowerCase() !== 'und') {
      return languageName(s.language);
    }
    return fallbackOrdinal != null ? `Spur ${fallbackOrdinal + 1}` : 'Spur';
  }
  function languageName(code) {
    const key = String(code || '').trim().toLowerCase();
    const iso2 = {
      deu: 'de', ger: 'de', eng: 'en', fra: 'fr', fre: 'fr', spa: 'es', ita: 'it',
      jpn: 'ja', kor: 'ko', zho: 'zh', chi: 'zh', por: 'pt', rus: 'ru', nld: 'nl',
      dut: 'nl', dan: 'da', swe: 'sv', nor: 'no', fin: 'fi', pol: 'pl', ces: 'cs', cze: 'cs',
    }[key] || key;
    try {
      const label = new Intl.DisplayNames(['de'], { type: 'language' }).of(iso2);
      if (label) return label.charAt(0).toUpperCase() + label.slice(1);
    } catch {}
    return key.toUpperCase();
  }
  function codecPretty(c) {
    const k = String(c || '').toLowerCase();
    if (k === 'truehd')   return 'TrueHD';
    if (k === 'eac3')     return 'E-AC-3';
    if (k === 'ac3')      return 'AC-3';
    if (k === 'dts')      return 'DTS';
    if (k === 'aac')      return 'AAC';
    if (k === 'opus')     return 'Opus';
    if (k === 'flac')     return 'FLAC';
    if (k === 'mp3')      return 'MP3';
    return c || '';
  }
  function channelPretty(s) {
    if (!s.channels) return '';
    if (s.channel_layout && s.channel_layout.trim()) {
      // Strip "(side)" / "(back)" qualifiers for compact display.
      const layout = String(s.channel_layout).replace(/\s*\([^)]+\)/g, '');
      return layout;
    }
    return `${s.channels}ch`;
  }
  function audioItemLabel(s, ordinal) {
    const lang     = languageLabel(s, ordinal);
    const codec    = codecPretty(s.codec);
    const channels = channelPretty(s);
    const profile  = s.profile ? ` (${String(s.profile).slice(0, 40)})` : '';
    const parts = [lang, codec, channels].filter(Boolean);
    return parts.join(' · ') + profile;
  }
  function audioShortLabel(s, ordinal) {
    if (s.language && s.language.trim() && s.language.toLowerCase() !== 'und') {
      return languageName(s.language);
    }
    // No language → show codec instead of useless "UND"
    return codecPretty(s.codec) || (ordinal != null ? `${ordinal + 1}` : '?');
  }
  function subsItemLabel(s, ordinal) {
    const lang  = languageLabel(s, ordinal);
    const title = s.title ? ` — ${s.title}` : '';
    const tags  = [];
    if (s.forced)           tags.push('forced');
    if (s.hearing_impaired) tags.push('SDH');
    const tagStr = tags.length ? ` [${tags.join(', ')}]` : '';
    const note   = s.extractable ? '' : ' (Bild-Untertitel — nicht unterstützt)';
    return `${lang} ${s.codec || ''}${title}${tagStr}${note}`.trim().replace(/\s+/g, ' ');
  }
  function subsShortLabel(s, ordinal) {
    if (s.language && s.language.trim() && s.language.toLowerCase() !== 'und') {
      return languageName(s.language);
    }
    return ordinal != null ? `${ordinal + 1}` : 'UND';
  }

  function setStreams(audioStreams, subtitleStreams, init) {
    const audio = Array.isArray(audioStreams)    ? audioStreams    : [];
    const subs  = Array.isArray(subtitleStreams) ? subtitleStreams : [];
    const wantAudio = (init && Number.isFinite(init.audioTrack)) ? init.audioTrack : 0;
    const wantSubs  = (init && Number.isFinite(init.subtitleTrack)) ? init.subtitleTrack : -1;

    // Audio: button stays visible always (user wants to see the spur even
    // when there's only one). With zero streams (probe failed/pending) we
    // show a placeholder. With one stream we show it as the current pick.
    audioBox.hidden = false;
    if (audio.length === 0) {
      audioMenu.innerHTML =
        '<div class="po-controls__menu__title">Tonspur</div>' +
        '<div class="po-controls__menu__item is-active" aria-disabled="true">— keine Info —</div>';
      audioLbl.textContent = 'Tonspur';
    } else {
      const items = audio.map((s, i) => {
        const lbl = audioItemLabel(s, i);
        const active = i === wantAudio;
        return `<button type="button" data-audio-track="${i}"
                  data-label="${escapeHtml(audioShortLabel(s, i))}"
                  class="po-controls__menu__item${active ? ' is-active' : ''}"
                  role="menuitemradio" aria-checked="${active}">${escapeHtml(lbl)}</button>`;
      }).join('');
      audioMenu.innerHTML =
        '<div class="po-controls__menu__title">Tonspur</div>' + items;
      const cur = audio[wantAudio] || audio[0];
      audioLbl.textContent = cur ? audioShortLabel(cur, wantAudio) : 'Tonspur';
    }

    // Subs: hide if 0 streams.
    if (subs.length === 0) {
      subsBox.hidden = true;
      subsBtn.classList.remove('is-on');
    } else {
      subsBox.hidden = false;
      const offActive = wantSubs < 0;
      let items = `<button type="button" data-subs-track="-1" data-label="Aus"
                    class="po-controls__menu__item${offActive ? ' is-active' : ''}"
                    role="menuitemradio" aria-checked="${offActive}">Aus</button>`;
      for (let si = 0; si < subs.length; ++si) {
        const s = subs[si];
        const active = s.index === wantSubs;
        const disabled = s.extractable ? '' : ' disabled';
        items += `<button type="button" data-subs-track="${s.index}"
                    data-label="${escapeHtml(subsShortLabel(s, si))}"
                    class="po-controls__menu__item${active ? ' is-active' : ''}"
                    role="menuitemradio" aria-checked="${active}"${disabled}>${escapeHtml(subsItemLabel(s, si))}</button>`;
      }
      subsMenu.innerHTML =
        '<div class="po-controls__menu__title">Untertitel</div>' + items;
      if (offActive) {
        subsLbl.textContent = 'Aus';
        subsBtn.classList.remove('is-on');
      } else {
        const idx = subs.findIndex(s => s.index === wantSubs);
        const cur = idx >= 0 ? subs[idx] : null;
        subsLbl.textContent = cur ? subsShortLabel(cur, idx) : 'An';
        subsBtn.classList.add('is-on');
      }
    }
  }

  // Initial state
  updateTime();
  updateBuffered();
  showBar();

  // ── Dispose ────────────────────────────────────────────────────────────────
  function dispose() {
    if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
    // B6 scrub-thumbnails: abort the in-flight index fetch + detach the
    // sheet-preload callbacks so nothing touches removed DOM afterwards.
    if (thumbsAbort) { try { thumbsAbort.abort(); } catch { /* noop */ } thumbsAbort = null; }
    if (sheetImg) { sheetImg.onload = null; sheetImg.onerror = null; sheetImg = null; }
    thumbsMeta = null; thumbsLoc = null; thumbsOff = true;
    document.removeEventListener('click', onDocClick, true);
    document.removeEventListener('fullscreenchange', syncFullscreenIcon);
    // <video> listeners are removed when the element itself is removed from
    // the DOM by ZapPlayer.destroy → modal.close → overlay.remove(). No leak.
    hostEl.innerHTML = '';
  }

  return {
    el: hostEl,
    dispose,
    updateLive,
    setStreams,
    setQualityLabel(text) { qualityLbl.textContent = String(text || ''); },
    setSpeedLabel(text)   { speedLbl.textContent   = String(text || ''); },
  };
}
